<template>
  <div class="spectacle-independence" v-if="dataLoaded">

    <div class="top">
      <chart-item class='chart-summary' :data="mainReports" :isLink="false" :colorOverride="'#2E96D6'" :percentColorOverride="'#2E96D6'" />
      <chart-item-filter v-if="showIolFilter(0)"
        :iols="iols"
        class='chart-summary'
        v-model="iolFilters[0]"
        @input="iolFilterChanged(0)"
      />
      <chart-item v-if="subReports[0].report"
        class='chart-summary'
        :isLink="false"
        :canClose="true"
        :colorOverride="'#DF5B96'"
        :percentColorOverride="'#2E96D6'"
        :data="subReports[0].report"
        @close="iolFilterClosed(subReports[0].report[0].name)"
      />
      <chart-item-filter v-if="showIolFilter(1)"
        :iols="iols"
        class='chart-summary'
        v-model="iolFilters[1]"
        @input="iolFilterChanged(1)"
      />
      <chart-item v-if="subReports[1].report"
        class='chart-summary'
        :isLink="false"
        :canClose="true"
        :colorOverride="'#F2C94C'"
        :percentColorOverride="'#2E96D6'"
        :data="subReports[1].report"
        @close="iolFilterClosed(subReports[1].report[0].name)"
      />
      <chart-item-filter v-if="showIolFilter(2)"
        :iols="iols"
        class='chart-summary'
        v-model="iolFilters[2]"
        @input="iolFilterChanged(2)"
      />
      <chart-item v-if="subReports[2].report"
        class='chart-summary'
        :isLink="false"
        :canClose="true"
        :colorOverride="'#F09639'"
        :percentColorOverride="'#2E96D6'"
        :data="subReports[2].report"
        @close="iolFilterClosed(subReports[2].report[0].name)"
      />
    </div>

    <div class="bottom">
      <chart-item-quad :data="mainReports" :reports="mainReports" :percentColorOverride="'#2E96D6'"
        :showResponses="true" :totalResponses="getResponseCount(mainReports)" />
      <div class="chart-item-spacer" v-if="!subReports[0].report" />
      <chart-item-quad v-if="subReports[0].report" :data="subReports[0].report" :reports="subReports[0].report" :percentColorOverride="'#DF5B96'"
        :showResponses="true" :totalResponses="getResponseCount(subReports[0].report)" />
      <div class="chart-item-spacer" v-if="!subReports[1].report" />
      <chart-item-quad v-if="subReports[1].report" :data="subReports[1].report" :reports="subReports[1].report" :percentColorOverride="'#F2C94C'"
        :showResponses="true" :totalResponses="getResponseCount(subReports[1].report)" />
      <div class="chart-item-spacer" v-if="!subReports[2].report" />
      <chart-item-quad v-if="subReports[2].report" :data="subReports[2].report" :reports="subReports[2].report" :percentColorOverride="'#F09639'"
        :showResponses="true" :totalResponses="getResponseCount(subReports[2].report)" />
    </div>

  </div>
</template>

<script>
  import ChartItem from '../ChartItem_v2'
  import ChartItemQuad from '../ChartItemQuad_v2'
  import ChartItemFilter from '../ChartItemFilter_v2'
  import SelectField from 'raypro_ui/src/components/ui/inputs/StyledSelect'
  import RpSprite from 'raypro_ui/src/components/ui/Sprite'
  import Dropdown from 'raypro_ui/src/components/ui/inputs/dropdown/Dropdown'
  import breakdown from '../../../../mixins/breakdown'
  // Chart.js
  import {Bar, Line as LineChartGenerator} from 'vue-chartjs/legacy'
  import {Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement} from 'chart.js'
  ChartJS.register(Title, Tooltip, Legend, BarElement, LineElement, CategoryScale, LinearScale, PointElement)

  export default {
    mixins: [breakdown],

    components: {
      Bar,
      LineChartGenerator,
      ChartItem,
      ChartItemQuad,
      ChartItemFilter,
      RpSprite, 
      SelectField, 
      'rp-dropdown': Dropdown,
    },

    data() {
      return {
        reports: [],
        globalReport: {},
        subReports: [
          {report: null, filter: '', color: '#DF5B96'},
          {report: null, filter: '', color: '#F2C94C'},
          {report: null, filter: '', color: '#F09639'},
        ],
      }
    },

    computed: {
      mainReports() {
        if (this.filterParams.includes('iol=') === false) {
          return this.reports
        } else {
          return [
            this.truncateReportToTimeSpan(this.globalReport[0]),
            this.truncateReportToTimeSpan(this.globalReport[1]),
            this.truncateReportToTimeSpan(this.globalReport[2])
          ]
        }
      },
    },

    methods: {
      async getReports() {
        let report = await this.getAllReportsPromise(this.filterParams)
        this.reports = this.filterReportDatas(report)
        this.globalReport = this.filterReportDatas({data: this.getGlobalReport(this.reports[0].name, this.reports[0].slug)})

        await this.getSubReports()
        this.dataLoaded = true
      },

      async getSubReports() {
        const models = this.getModelNamesFromFilterParams()
        for (const model of models) {
          let index = 0
          const filterItem = this.iolFilters.find(x => x && x.model === model)
          if (filterItem) {
            index = this.iolFilters.indexOf(filterItem)
          }

          const filterParams = this.getSubReportFilterParams(models.indexOf(model))
          let report = await this.getAllReportsPromise(filterParams)
          report = this.filterReportDatas(report)
          report[0].name = model
          this.subReports[index].report = report
          this.subReports[index].filter = filterParams
        }
      },

      updateSubReports() {
        this.subReports.forEach((subReport) => {
          if (subReport.report && !this.iolFilters.find((x) => x && x.model === subReport.report[0].name)) {
            subReport.filter = ''
            subReport.report = null
          }
        })
      },

      downloadReport(index) {
        this.getExportReportPromise(this.reports[index].id, this.reports[index].slug).then((res) => {
          this.downloadResult(res)
        })
      },

      filterReportDatas(data) {
        return data.data.filter((entry) => {
          if (entry.slug === 'spectacle_independence') {
            return entry
          }
          return false
        })
      },

      getResponseCount(reports) {
        let responses = []
        reports.forEach((report) => {
          let data = report.report_datas.data
          if (data.error === undefined) {
            Object.keys(data).forEach((key) => {
              responses.push(data[key])
            })
          }
        })

        responses = responses.reduce((a, b) => a + b, 0)
        return responses
      },

      setDownloadOptions() {
        this.$emit('setDownloadOptions', ['Distance', 'Intermediate', 'Near'])
      },

      iolFilterChanged(index) {
        if (this.iolFilters[index]) {
          if (this.iolFilters[index] && !this.subReports.find(x => x.report && x.report[0].name === this.iolFilters[index].model)) {
            this.$emit('addIolFilter', this.iolFilters[index].id)
          } else {
            this.iolFilters[index] = null
          }
        }
      },

      iolFilterClosed(model) {
        const iol = this.iolFilters.find((x) => x && x.model === model)
        this.subReports[this.iolFilters.indexOf(iol)].filter = ''
        this.subReports[this.iolFilters.indexOf(iol)].report = null
        this.iolFilters[this.iolFilters.indexOf(iol)] = null
        this.$emit('removeIolFilter', iol.id)
      },
    }
  }
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .spectacle-independence
    display: flex
    flex-direction: column
    width: 100%
    padding: 10px
    margin-right: -30px
    box-shadow: $color-shadow
    background-color: transparent //$color-panel-bg

    .bottom
      display: flex
      flex-direction: row
      gap: 40px

      .quad-chart
        display: flex
        flex-wrap: wrap
        background: transparent //$color-panel-bg

        .quad-item
          width: 50%
          height: 50%
          border-radius: 20px
          background: $color-panel-bg

      .chart-item-spacer
        width: calc(25% - 30px)

    .top
      display: flex
      flex-direction: column
      justify-content: flex-start
      width: 100%
      gap: 40px
      
      @include media($media-small)
        flex-direction: row

      .chart-summary
        margin-right: 0px

</style>



// WEBPACK FOOTER //
// src/components/pages/Dashboard/Breakdowns/SpectacleIndependence_v2.vue